import { notifier } from "../../../../../core/notifier/notifier";
import { ModalNestedPosition } from "../../../../../util/modal-nested-position";
import { Roles } from "../../../../../constants/roles";
import { ReviewConfigEditorForm } from "./review-config-editor-form";

type UnknownRecord = Record<string, unknown>;

const toInt = (value: unknown, fallback = 0): number => {
    const n = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(n) ? n : fallback;
};

const isRecord = (value: unknown): value is UnknownRecord =>
    value !== null && typeof value === "object" && !Array.isArray(value);

type CategoryReviewEditorCoreHandler = import("../category-review-editor-handler").CategoryReviewEditorHandler;

export class ReviewConfigEditorHandler {
    public coreHandler: CategoryReviewEditorCoreHandler;

    private readonly form: ReviewConfigEditorForm;

    public constructor(coreHandler: CategoryReviewEditorCoreHandler) {
        this.coreHandler = coreHandler;
        this.form = new ReviewConfigEditorForm(coreHandler.modalEl);
    }

    public onReviewPanelInput(event: Event): void {
        this.coreHandler.onReviewPanelInput(event);
    }

    public onReviewPanelChange(event: Event): void {
        this.coreHandler.onReviewPanelChange(event);
    }

    public onReviewPanelClick(event: Event): void {
        this.coreHandler.onReviewPanelClick(event);
    }

    public isReviewSettingsOpen(): boolean {
        const c = this.coreHandler;
        return c.reviewSettingsModalEl instanceof HTMLElement && c.reviewSettingsModalEl.classList.contains("visible");
    }

    public getReviewSettingsWindowEl(): HTMLElement | null {
        const c = this.coreHandler;
        if (!(c.reviewSettingsModalEl instanceof HTMLElement)) return null;
        const windowEl = this.form.getReviewSettingsWindow(c.reviewSettingsModalEl);
        return windowEl instanceof HTMLElement ? windowEl : null;
    }

    public openReviewSettingsModal(configId: number): void {
        const c = this.coreHandler;
        if (configId <= 0) {
            c.setStatus("review_config_id is not assigned", "error");
            return;
        }
        if (!(c.reviewSettingsModalEl instanceof HTMLElement)) return;
        if (!(c.reviewSettingsPanelEl instanceof HTMLElement)) return;
        c.reviewSettingsConfigId = configId;
        if (c.reviewSettingsTitleEl instanceof HTMLElement) c.reviewSettingsTitleEl.textContent = "Review Questions";
        c.renderer.renderReviewTable(c, c.reviewSettingsPanelEl);
        ModalNestedPosition.openAsNested({
            modalRootEl: c.reviewSettingsModalEl,
            windowSelector: `[data-role~="${Roles.CATEGORY_REVIEW_EDITOR_REVIEW_SETTINGS_WINDOW}"]`,
            parentModalSelector: `[data-role="${Roles.CATEGORY_REVIEW_EDITOR_MODAL}"].visible, [data-role="${Roles.SEARCH_MENU_POST_PRODUCT_PICKER_CONTAINER}"].visible, [data-role="${Roles.SEARCH_CATEGORY_MODAL}"].visible, [data-role="${Roles.SEARCH_BRAND_MODAL}"].visible, [data-role="${Roles.SEARCH_USER_MODAL}"].visible`,
            overlayEl: c.overlayEl,
        });
    }

    public closeReviewSettingsModal(): void {
        const c = this.coreHandler;
        if (!(c.reviewSettingsModalEl instanceof HTMLElement)) return;
        const windowEl = this.getReviewSettingsWindowEl();
        c.reviewSettingsConfigId = 0;
        ModalNestedPosition.closeModal({ modalEl: c.reviewSettingsModalEl });
        if (windowEl instanceof HTMLElement) {
            windowEl.classList.remove("is-nested-modal");
            windowEl.style.removeProperty("position");
            windowEl.style.removeProperty("left");
            windowEl.style.removeProperty("top");
            windowEl.style.removeProperty("right");
            windowEl.style.removeProperty("bottom");
        }
    }

    public handleReviewSettingsModalClick(event: Event): void {
        const c = this.coreHandler;
        if (!(c.reviewSettingsModalEl instanceof HTMLElement)) return;
        if (event.target !== c.reviewSettingsModalEl) return;
        this.closeReviewSettingsModal();
    }

    public handleReviewSettingsClose(event: Event): void {
        event.preventDefault();
        this.closeReviewSettingsModal();
    }

    public async handleReviewSettingsSave(event: Event): Promise<void> {
        const c = this.coreHandler;
        event.preventDefault();
        await c.saveAllChanges();
        if (!c.isSubmitting) this.closeReviewSettingsModal();
    }

    public async handleReviewSettingsAdd(event: Event): Promise<void> {
        const c = this.coreHandler;
        event.preventDefault();
        if (c.isLoading || c.isSubmitting) return;
        const newConfigId = this.getNextAvailableReviewConfigId();
        if (newConfigId <= 0) {
            c.setStatus("Failed to allocate review config ID", "error");
            notifier.error("category_review_editor_review_config_allocate_failed");
            return;
        }
        c.configs.push({
            review_config_id: newConfigId,
            name: `Review Config ${newConfigId}`,
            description: `Review Config ${newConfigId}`,
            is_active: 1,
            _isNew: true,
            _isDeleted: false,
        });
        if (!c.questionsByConfig.has(newConfigId)) c.questionsByConfig.set(newConfigId, []);
        c.reviewSettingsConfigId = newConfigId;
        await c.createReviewQuestion(newConfigId);
    }

    public async handleReviewSettingsAddQuestion(event) {
        const c = this.coreHandler;
        event.preventDefault();
        if (c.isLoading || c.isSubmitting) return;
        let configId = toInt(c.reviewSettingsConfigId, 0);
        if (configId <= 0) {
            const firstConfigId = Array.from(
                new Set([
                    ...c.configs.map((config) => toInt(config?.review_config_id, 0)),
                    ...Array.from(c.questionsByConfig.keys()).map((id) => toInt(String(id), 0)),
                ]),
            )
                .filter((id) => id > 0)
                .sort((a, b) => a - b)[0];
            configId = toInt(firstConfigId, 0);
        }
        if (configId <= 0) {
            c.setStatus("No review config found", "error");
            return;
        }
        c.reviewSettingsConfigId = configId;
        await c.createReviewQuestion(configId);
    }

    public async handleReviewSettingsAddConfig(event) {
        const c = this.coreHandler;
        event.preventDefault();
        if (c.isLoading || c.isSubmitting) return;
        const newConfigId = this.getNextAvailableReviewConfigId();
        if (newConfigId <= 0) {
            c.setStatus("Failed to allocate review config ID", "error");
            notifier.error("category_review_editor_review_config_allocate_failed");
            return;
        }
        c.configs.push({
            review_config_id: newConfigId,
            name: `Review Config ${newConfigId}`,
            description: `Review Config ${newConfigId}`,
            is_active: 1,
            _isNew: true,
            _isDeleted: false,
        });
        if (!c.questionsByConfig.has(newConfigId)) c.questionsByConfig.set(newConfigId, []);
        c.reviewSettingsConfigId = newConfigId;
        c.reindexLocalState();
        if (c.reviewSettingsTitleEl instanceof HTMLElement) c.reviewSettingsTitleEl.textContent = "Review Questions";
        if (c.reviewSettingsPanelEl instanceof HTMLElement) c.renderer.renderReviewTable(c, c.reviewSettingsPanelEl);
        c.setStatus(`Added review config (${newConfigId}) (not saved)`, "ok");
    }

    public ensureReviewConfigForChild(childId: number): number {
        const c = this.coreHandler;
        if (childId <= 0) return 0;
        const child = c.children.find((row) => toInt(row.category_id, 0) === childId);
        if (!child || child._isDeleted) return 0;
        if (child._isLocked) {
            c.setStatus("--99 OTHERS cannot be edited", "error");
            return 0;
        }
        const existingConfigId = toInt(child?._draftReviewConfigId ?? child?.review_config_id ?? 0, 0);
        if (existingConfigId > 0) return existingConfigId;
        const newConfigId = this.getNextAvailableReviewConfigId();
        if (newConfigId <= 0) {
            c.setStatus("Failed to allocate review config ID", "error");
            notifier.error("category_review_editor_review_config_allocate_failed");
            return 0;
        }
        c.configs.push({
            review_config_id: newConfigId,
            name: `Review Config ${newConfigId}`,
            description: `Review Config ${newConfigId}`,
            is_active: 1,
            _isNew: true,
            _isDeleted: false,
        });
        if (!c.questionsByConfig.has(newConfigId)) c.questionsByConfig.set(newConfigId, []);
        child._draftReviewConfigId = newConfigId;
        c.reindexLocalState();
        c.renderer.renderChildTable(c);
        c.renderer.renderReviewTable(c);
        c.setStatus(`Added review config (${newConfigId}) (not saved)`, "ok");
        return newConfigId;
    }

    public getReviewConfigRow(configId: number) {
        const c = this.coreHandler;
        const safeConfigId = toInt(configId, 0);
        if (safeConfigId <= 0) return null;
        return c.configs.find((row) => toInt(row?.review_config_id, 0) === safeConfigId) || null;
    }

    public canSelectReviewConfig(configId: number): boolean {
        const c = this.coreHandler;
        const safeConfigId = toInt(configId, 0);
        if (safeConfigId <= 0) return false;
        const configRow = this.getReviewConfigRow(safeConfigId);
        if (configRow && configRow._isDeleted === true) return false;
        if (configRow && configRow._isNew === true) return false;
        if (c.isReviewConfigEditing(safeConfigId)) return false;
        if (c.isReviewConfigDirty(safeConfigId)) return false;
        if (c.isReviewConfigNew(safeConfigId)) return false;
        if (c.isReviewConfigDeleted(safeConfigId)) return false;
        return true;
    }

    public handleReviewSettingsSelectConfig(configId: number): void {
        const c = this.coreHandler;
        const safeConfigId = toInt(configId, 0);
        if (safeConfigId <= 0) return;
        if (!this.canSelectReviewConfig(safeConfigId)) {
            c.setStatus("Save changes before selecting", "error");
            return;
        }
        const childId = toInt(c.reviewSettingsTargetChildId, 0);
        if (childId <= 0) return c.setStatus("No target category selected", "error");
        const child = c.children.find((row) => toInt(row.category_id, 0) === childId);
        if (!child || child._isDeleted) return c.setStatus("Target category not found", "error");
        if (child._isLocked) return c.setStatus("--99 OTHERS cannot be edited", "error");
        child._draftReviewConfigId = safeConfigId;
        const parentId = toInt(child.parent_category_id, 0);
        if (parentId > 0) c.selectedChildParentId = parentId;
        c.reindexLocalState();
        c.renderer.renderChildTable(c);
        this.closeReviewSettingsModal();
        c.setStatus(`Linked review config (${safeConfigId}) (not saved)`, "ok");
    }

    public getNextAvailableReviewConfigId() {
        const c = this.coreHandler;
        const usedIds = new Set();
        c.configs.forEach((item) => {
            const id = toInt(item?.review_config_id, 0);
            if (id > 0) usedIds.add(id);
        });
        c.children.forEach((item) => {
            const id = toInt(item?._draftReviewConfigId ?? item?.review_config_id ?? 0, 0);
            if (id > 0) usedIds.add(id);
        });
        c.questionsByConfig.forEach((_list, configId) => {
            const id = toInt(configId, 0);
            if (id > 0) usedIds.add(id);
        });
        let nextId = 1;
        while (usedIds.has(nextId)) nextId += 1;
        return nextId;
    }

    public extractReviewConfigIdFromCreateResponse(payload) {
        const candidates = [];
        if (isRecord(payload)) {
            if (isRecord(payload.data)) candidates.push(payload.data);
            if (isRecord(payload.data) && isRecord(payload.data.data)) candidates.push(payload.data.data);
            if (isRecord(payload.item)) candidates.push(payload.item);
            if (isRecord(payload.result)) candidates.push(payload.result);
            candidates.push(payload);
        }
        for (const candidate of candidates) {
            const id = toInt(candidate.review_config_id ?? candidate.id ?? candidate.config_id ?? 0, 0);
            if (id > 0) return { id, data: candidate };
        }
        return { id: 0, data: {} };
    }

    public remapReviewConfigIdInLocalState(oldId, newId) {
        const c = this.coreHandler;
        const safeOldId = toInt(oldId, 0);
        const safeNewId = toInt(newId, 0);
        if (safeOldId <= 0 || safeNewId <= 0 || safeOldId === safeNewId) return;
        c.configs.forEach((item) => {
            if (toInt(item.review_config_id, 0) === safeOldId) item.review_config_id = safeNewId;
        });
        c.children.forEach((child) => {
            if (toInt(child.review_config_id, 0) === safeOldId) child.review_config_id = safeNewId;
            if (toInt(child._draftReviewConfigId ?? 0, 0) === safeOldId) child._draftReviewConfigId = safeNewId;
        });
        if (c.questionsByConfig.has(safeOldId)) {
            const oldList = c.questionsByConfig.get(safeOldId) || [];
            oldList.forEach((question) => {
                question.review_config_id = safeNewId;
            });
            const existingList = c.questionsByConfig.get(safeNewId) || [];
            c.questionsByConfig.set(safeNewId, [...existingList, ...oldList]);
            c.questionsByConfig.delete(safeOldId);
        }
        if (toInt(c.reviewSettingsConfigId, 0) === safeOldId) c.reviewSettingsConfigId = safeNewId;
        c.reindexLocalState();
    }

    public scrollReviewSettingsTableToLeft() {
        const c = this.coreHandler;
        if (!(c.reviewSettingsPanelEl instanceof HTMLElement)) return;
        const wrapEl = this.form.getTableWrap(c.reviewSettingsPanelEl);
        if (!(wrapEl instanceof HTMLElement)) return;
        wrapEl.scrollLeft = 0;
        wrapEl.scrollTop = 0;
    }

    public animateHorizontalScroll(wrapEl, toLeft, durationMs = 260) {
        if (!(wrapEl instanceof HTMLElement)) return;
        const fromLeft = wrapEl.scrollLeft;
        const maxLeft = Math.max(0, wrapEl.scrollWidth - wrapEl.clientWidth);
        const targetLeft = Math.max(0, Math.min(maxLeft, Number(toLeft) || 0));
        if (Math.abs(targetLeft - fromLeft) < 1) {
            wrapEl.scrollLeft = targetLeft;
            return;
        }
        const startTs = performance.now();
        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
        const tick = (nowTs) => {
            const elapsed = nowTs - startTs;
            const progress = Math.max(0, Math.min(1, elapsed / durationMs));
            const eased = easeInOutCubic(progress);
            wrapEl.scrollLeft = fromLeft + (targetLeft - fromLeft) * eased;
            if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }

    public scrollReviewSettingsTableToRight() {
        const c = this.coreHandler;
        if (!(c.reviewSettingsPanelEl instanceof HTMLElement)) return;
        const wrapEl = this.form.getTableWrap(c.reviewSettingsPanelEl);
        if (!(wrapEl instanceof HTMLElement)) return;
        this.animateHorizontalScroll(wrapEl, wrapEl.scrollWidth, 280);
    }

    public scrollWrapToElementCenter(wrapEl, targetEl, behavior = "smooth") {
        if (!(wrapEl instanceof HTMLElement) || !(targetEl instanceof HTMLElement)) return;
        const wrapRect = wrapEl.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();
        const deltaLeft = targetRect.left - wrapRect.left - (wrapRect.width - Math.max(targetRect.width, 1)) / 2;
        const nextLeft = Math.max(0, wrapEl.scrollLeft + deltaLeft);
        if (behavior === "auto") {
            wrapEl.scrollLeft = nextLeft;
            return;
        }
        this.animateHorizontalScroll(wrapEl, nextLeft, 280);
    }

    public focusNewReviewQuestionEditor(questionId) {
        const c = this.coreHandler;
        const safeQuestionId = toInt(questionId, 0);
        if (safeQuestionId === 0) return;
        const targetPanel =
            c.reviewSettingsPanelEl instanceof HTMLElement && this.isReviewSettingsOpen()
                ? c.reviewSettingsPanelEl
                : c.panelReviewEl;
        if (!(targetPanel instanceof HTMLElement)) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const wrapEl = this.form.getTableWrap(targetPanel);
                const questionInput = this.form.getQuestionTextThInput(targetPanel, safeQuestionId);
                const questionControl = this.form.getQuestionControl(targetPanel, safeQuestionId);
                const focusTarget =
                    questionInput instanceof HTMLInputElement
                        ? questionInput
                        : questionControl instanceof HTMLElement
                          ? questionControl
                          : null;
                if (wrapEl instanceof HTMLElement) {
                    if (focusTarget instanceof HTMLElement)
                        this.scrollWrapToElementCenter(wrapEl, focusTarget, "smooth");
                    else this.animateHorizontalScroll(wrapEl, wrapEl.scrollWidth, 280);
                }
                if (questionInput instanceof HTMLInputElement) {
                    setTimeout(() => questionInput.focus({ preventScroll: true }), 180);
                    return;
                }
                if (questionControl instanceof HTMLElement) {
                    setTimeout(() => questionControl.focus({ preventScroll: true }), 180);
                }
            });
        });
    }
}
